import { useAuthStore } from '@/stores/authStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { ApiError } from '../api/errors'

export interface Identity {
  userId: string | null
  workspaceId: string | null
  /** Bumps on every login and logout, so the same user signing in again is a new identity. */
  epoch: number
}

export type Scope = 'user' | 'workspace'

let epoch = 0
const inFlight = new Map<AbortController, Scope>()

export function bumpEpoch(): void {
  epoch += 1
}

export function currentIdentity(): Identity {
  return {
    userId: useAuthStore.getState().user?.id ?? null,
    workspaceId: useWorkspaceStore.getState().activeWorkspace?.id ?? null,
    epoch,
  }
}

/**
 * Snapshot the identity now. The returned check stays true while the user and the session (and, for
 * 'workspace', the active workspace) are the ones of the snapshot; store actions write only while it holds.
 */
export function identityCheck(scope: Scope = 'workspace'): () => boolean {
  const start = currentIdentity()
  return () => {
    const now = currentIdentity()
    return now.userId === start.userId && now.epoch === start.epoch
      && (scope === 'user' || now.workspaceId === start.workspaceId)
  }
}

/** The response belongs to a user, session or workspace that is no longer active. */
export class StaleIdentityError extends ApiError {
  constructor() {
    super({ status: 0, code: 'stale_identity', detail: 'The account or workspace changed before the answer arrived.' })
    this.name = 'StaleIdentityError'
  }
}

/**
 * Run `fn` for the identity active now. It gets a signal that `abortScope` cancels; when it settles
 * after the user, the session, or (for 'workspace') the active workspace changed, the result is
 * dropped and the call rejects with StaleIdentityError — callers write only on resolve.
 */
export async function scopedRequest<T>(scope: Scope, fn: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const current = identityCheck(scope)
  const controller = new AbortController()
  inFlight.set(controller, scope)
  try {
    const result = await fn(controller.signal)
    if (!current()) throw new StaleIdentityError()
    return result
  } catch (error) {
    if (!current()) throw new StaleIdentityError()
    throw error
  } finally {
    inFlight.delete(controller)
  }
}

/** Cancel in-flight scoped requests: workspace-scoped ones on a switch, every one on login/logout. */
export function abortScope(which: 'workspace' | 'all'): void {
  for (const [controller, scope] of inFlight) {
    if (which === 'all' || scope === 'workspace') {
      inFlight.delete(controller)
      controller.abort()
    }
  }
}
